












































































import { Vue, Component, Watch } from 'vue-property-decorator';

import { Surface, Typography } from '@/components/atoms';
import ModalOptions from '@/components/Modal/ModalOptions';
import Page from '@/componentsV2/molecules/Page/Page.vue';
import { BreadcrumbData } from '@/componentsV2/molecules/Breadcrumbs/Breadcrumbs.vue';
import {
  CampaignStatus,
  CoinSettings,
  CustomerSettings,
  ExpirationSettings,
  LocationSettings
} from '@/components/organisms/OfferFreeCoins/FormSteps';
import flagKeys from '@/constants/flagKeys';

import OfferFreeCoinsService from '@/api/OfferFreeCoinsService';

import DateUtils from '@/utils/DateUtils';
import eventBus from '@/utils/eventBus';
import isMobile from '@/utils/mobile-check';
import { handleCompleteTask } from '@/utils/start-guide';
import { EStartGuideTaskSlug } from '@/models/start-guide';

import segmentIo from '@/utils/segment.io';

import {
  TCustomerLimitType,
  TDeadLineType,
  TVisibilityType
} from '@/models/marketingCenter/freeCoins';
import OverviewSidebar from '@/components/organisms/OfferFreeCoins/OverviewSidebar.vue';
import { isRemoteLoading } from '@/utils/remote-loading';

@Component({
  components: {
    Surface,
    Typography,
    CoinSettings,
    CustomerSettings,
    ExpirationSettings,
    CampaignStatus,
    LocationSettings,
    Page,
    OverviewSidebar
  }
})
export default class OfferFreeCoins extends Vue {
  OfferService = new OfferFreeCoinsService();
  loading: boolean = false;
  error: string[] = [];

  coins: string = '';
  customerLimitType: TCustomerLimitType = 'no-limit';
  visibilityType: TVisibilityType = this.initialVisibilityType;
  customerLimit?: number = 0;
  customerDailyLimit?: number = 0;
  onlyNewCustomers: boolean = true;
  expirationType: TDeadLineType = 'no-deadline';
  expiration: string = '';
  immediateActivation: boolean = true;
  sidebarPosition: 'relative' | 'sticky' = 'relative';
  expirationPlaceholderValue = '';

  get isMobileDevice() {
    return isMobile();
  }

  get title() {
    return 'Welcome Coins Campaign';
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get isRemoteLoading() {
    return isRemoteLoading();
  }

  @Watch('expirationType', { deep: true })
  removeDeadlineDateError() {
    this.error = this.error.filter(err => err !== 'deadline-date');
  }

  @Watch('coins')
  validate() {
    const newErrors = [];

    if (this.expirationType === 'deadline-date' && !this.expiration) {
      newErrors.push('deadline-date');
    }

    if (Number(this.coins) <= 0) {
      newErrors.push('coins');
    }

    if (
      this.customerLimitType === 'total-customer' &&
      Number(this.customerLimit) <= 0
    ) {
      newErrors.push('customerLimit');
    }

    if (
      this.customerLimitType === 'daily-customer' &&
      Number(this.customerDailyLimit) <= 0
    ) {
      newErrors.push('customerDailyLimit');
    }

    this.error = newErrors;

    return newErrors.length === 0;
  }

  handleCoinsModalOpened() {
    segmentIo.modal('Free Coins/How many Coins');
  }

  handleSaveClick() {
    this.$analytics({
      eventCategory: 'Free Coins Campaign Creation | Create campaign button',
      eventAction: 'click',
      eventLabel: 'Clicked to save campaign'
    });
  }

  async createNewOfferFreeCoinCampaign() {
    const validation = this.validate();

    if (!validation) {
      this.$notification('Please fill the highlighted fields below', 'error');
      return;
    }

    // If the AllowRepeatingCustomers field is true, customers who have already redeemed coins will continue to be able to redeem new coins
    const campaign: any = {
      coinsReward: Number(this.coins),
      allowRepeatingCustomers: this.onlyNewCustomers ? false : true,
      limitType: this.customerLimitType,
      status: this.immediateActivation ? 'active' : 'inactive',
      visibilityType: this.visibilityType
    };

    if (this.expirationType !== 'no-deadline') {
      const fromDate = new Date(this.expiration);
      const parsedDate: string = fromDate.toISOString();

      campaign.dateLimit = parsedDate;
    } else {
      campaign.dateLimit = undefined;
    }

    if (this.customerLimitType === 'total-customer') {
      campaign.customerLimit = this.customerLimit;
    }

    if (this.customerLimitType === 'daily-customer') {
      campaign.customerLimit = this.customerDailyLimit;
    }

    this.loading = true;
    const response = await this.OfferService.createOfferFreeCoin(campaign);

    eventBus.$emit('closeModal');
    this.loading = false;

    if (response.status === 201) {
      const params = this.$route.params;

      handleCompleteTask(EStartGuideTaskSlug.WELCOME_COINS);

      if (params.taskSlug) {
        this.$router.push('/home');

        return;
      }

      segmentIo.modal('Free Coins/Success');

      if (!this.isRemoteLoading) {
        this.$router.push('/marketing-center/offer-free-coins');
      }

      window.parent.postMessage(
        {
          type: 'welcome-coins:campaign-created',
          data: response.data
        },
        '*'
      );

      eventBus.$emit(
        'openSubModal',
        new ModalOptions(
          ``,
          ``,
          'none',
          true,
          ``,
          ``,
          ``,
          false,
          'offerFreeCoinsCampaignCreationFeedback',
          false,
          true,
          true,
          true,
          true
        ),
        {
          immediateActivation: this.immediateActivation,
          onCloseModal: () => {
            if (this.isRemoteLoading)
              this.$router.push('/marketing-center/offer-free-coins');
          }
        }
      );
    }
  }

  changeCustomerLimit(value: string) {
    switch (this.customerLimitType) {
      case 'total-customer':
        this.customerLimit = Number(value);
        break;

      case 'daily-customer':
        this.customerDailyLimit = Number(value);
        break;

      default:
        break;
    }
  }

  changeExpirationValue(date: string) {
    this.expiration = date;
    this.expirationPlaceholderValue = DateUtils.formattedDate({ date });
  }

  toggleNewCustomers(value: boolean) {
    this.onlyNewCustomers = value;
  }

  createObserver() {
    const target: HTMLElement = this.$refs.target as HTMLElement;

    if (this.isMobileDevice) return;

    const observer = new IntersectionObserver(async entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.sidebarPosition = 'relative';
        } else {
          this.sidebarPosition = 'sticky';
        }
      });
    });

    if (target) {
      observer.observe(target);
    }
  }

  get businessType() {
    return this.$store.getters['business/getBusinessInfo'].business.type;
  }

  // With the flag on a both business creates local campaigns, as the native
  // form does. An online one stays nationwide: the API refuses `local` for it.
  get initialVisibilityType() {
    if (this.businessType === 'physical') return 'local';
    if (this.isPhysicalOnline && this.isWelcomeCoinsV2) return 'local';
    return 'nationwide';
  }

  get isPhysicalOnline() {
    return this.businessType === 'physical_online';
  }

  /*
   * The host's native form sits behind the same flag. With it on, this form
   * drops the steps the redesign removed (TWISM-20383), and those fields keep
   * their initial values: the business type's visibility, only new members,
   * no daily limit, active. The backend requires all of them.
   */
  get isWelcomeCoinsV2() {
    return this.flags.includes(flagKeys.welcomeCoinsInterfaceV2);
  }

  back() {
    this.$router.push('/marketing-center/offer-free-coins');
  }

  get breadcrumbs() {
    const breadcrumbList: BreadcrumbData[] = [
      {
        label: 'Campaigns',
        path: '/marketing-center/offer-free-coins'
      }
    ];

    if (!this.isMobileDevice) {
      breadcrumbList.push({
        label: 'Create a welcome Coins campaign'
      });
    }

    return breadcrumbList;
  }

  mounted() {
    if (this.$route.query.coins && Number(this.$route.query.coins) > 0) {
      this.coins = this.$route.query.coins as string;
    }
    this.createObserver();
    eventBus.$on(
      'createNewOfferFreeCoinCampaign',
      this.createNewOfferFreeCoinCampaign
    );
  }

  beforeDestroy() {
    eventBus.$off(
      'createNewOfferFreeCoinCampaign',
      this.createNewOfferFreeCoinCampaign
    );
  }
}
